import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";

interface LoadingAnimationProps {
  onAnimationComplete: () => void; // Callback prop (called after fade-out)
  /**
   * Optional external control for fade-out. When provided, overrides the internal fade-out state.
   */
  fadeOut?: boolean;
  /**
   * Optional external text. When provided, the type/delete animation is disabled and this text is shown as-is.
   */
  text?: string | string[];
  /**
   * If true, the internal typing animation loops indefinitely (no auto fade-out).
   * Defaults to false (auto fade-out after cycling built-in texts once).
   */
  loop?: boolean;

  height?: string; // Optional height prop (not used in current implementation)
  position?: "absolute" | "relative"; // Optional position prop (not used in current implementation)
}

const LoadingAnimation: React.FC<LoadingAnimationProps> = ({
  onAnimationComplete,
  fadeOut,
  height = "calc(100% - 16px)",
  position = "absolute",
  text: externalText,
  loop = false,
}) => {
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(200);
  const [isFadeOut, setIsFadeOut] = useState(false);
  const { t } = useTranslation();

  const providedList = Array.isArray(externalText) ? externalText : undefined;

  const texts = useMemo(
    () => providedList ?? [t("hello"), t("howCanIHelpYou")],
    [providedList, t],
  );

  useEffect(() => {
    // If external text is a single string, do not run typing animation; if it's a list, type through it
    if (typeof externalText === "string") return;
    if (isFadeOut) return; // Stop typing if fade-out has started

    const handleTyping = () => {
      const currentIndex = loopNum % texts.length;
      const fullText = texts[currentIndex];
      const updatedText = isDeleting
        ? fullText.substring(0, displayText.length - 1)
        : fullText.substring(0, displayText.length + 1);

      setDisplayText(updatedText);

      if (!isDeleting && updatedText === fullText) {
        // Pause before starting to delete
        setTimeout(() => setIsDeleting(true), 1800);
        setTypingSpeed(50);
      } else if (isDeleting && updatedText === "") {
        // Move to next text after deletion
        setIsDeleting(false);
        setTypingSpeed(100);
        setLoopNum((prev) => prev + 1);
      }
    };

    const typingTimer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(typingTimer);
  }, [displayText, isDeleting, typingSpeed, loopNum, isFadeOut, externalText, texts]);

  useEffect(() => {
    if (typeof externalText === "string") {
      setDisplayText(externalText);
    }
  }, [externalText]);

  useEffect(() => {
    // Trigger fade-out after completing all text loops when loop=false and no external text is provided
    if (!loop && typeof externalText !== "string" && loopNum === texts.length) {
      setIsFadeOut(true);
    }
  }, [loopNum, texts.length, loop, externalText]);

  const isFading = fadeOut ?? isFadeOut;

  const handleTransitionEnd = () => {
    if (isFading) {
      onAnimationComplete(); // Notify parent after fade-out
    }
  };

  return (
    <Grid
      size={{ xs: 12 }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height,
        width: "calc(100% - 16px)",
        position,
        zIndex: 1,
        opacity: isFading ? 0 : 1, // Control opacity for fade-out
        transition: "opacity 1s", // Duration of fade-out
      }}
      onTransitionEnd={handleTransitionEnd} // Listen for transition completion
    >
      <div className="circles">
        <div className="circle"></div>
        <div className="circle"></div>
        <div className="circle"></div>
        <div className="circle"></div>
      </div>
      <Box sx={{ position: "absolute", zIndex: 10, textAlign: "center" }}>
        <Typography
          variant="h4"
          color="text.primary"
          className="typing-effect"
          sx={{ transition: "opacity 0.6s", fontWeight: 900 }}
        >
          {displayText}
        </Typography>
      </Box>
    </Grid>
  );
};

export default LoadingAnimation;
